import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Divider, Form } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import moment from 'moment';

import guarantorYellow from 'assets/images/guarantor-yellow.svg';
import InputBorderNone from 'components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import { File, Loan, Task, TaskState } from 'gql/graphql';
import { useCreateResponseTask, useRemoveTaskResponse } from 'services/task/mutations';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import ModalCompleteProfile from './ModalComplete';
import ModalFinishTask from './ModalFinishTask';
import FormEdit from './FormEdit';
import { useGetTaskResponseComments, useGetTaskResponsesByTask } from 'services/task/querys';
import TagOwnerState from '../../Step2/CardOwnershipNew/TagOwnerState';
import { useCreateOwnership, useDeleteOwnership } from '../../../../../../../services/ownership/mutations';
import { LoanContext } from '../../../../Dashboard';
import IssuesTaskResponse from 'scenes/Private/components/IssuesTaskResponse/IssuesTaskResponse';
import ErrorBox from 'components/Atoms/Alerts/ErrorBox';
import alertIcon from 'assets/images/alert-triangle.svg';
import { DATE_FORMAT } from '@common/utils/constants/dates.constanst';
import { TotalPercentageContext } from '../Step3';

export interface Ownership {
  _id?: string;
  key: string;
  name: string;
  lastname: string;
  ownership: number;
  dateBirth: string;
  email: string;
  phone: string;
  ssn: string;
  address: string;
  state: string;
  stakes: any;
  files: File[];
  taskResponse: any;
}

interface Props {
  index?: any;
  ownership: Ownership;
  disabledForm?: boolean;
  ownerships?: Ownership[];
  closeTask?: Function;
  isActionRemoved?: boolean;
  entity?: any;
  onDeleteOwner?: any;
  isExpand?: any;
  setNewPercentageLiveEdit?: any;
  handlerSwitchExpandCard?: any;
}

let isUpdatePercentage = false;

// Helper function to remove __typename from files
const cleanFileLocalUpload = (files: any): any => {
  if (!files) return files;
  
  if (Array.isArray(files)) {
    const cleanedFiles = files
      .map((file: any) => {
        if (file?.state) return undefined;

        if (file && typeof file === 'object') {
          delete file.__typename;
          return file;
        }
        return file;
      })
      .filter((file: any) => file !== null && file !== undefined);
    
    // Si después de limpiar el array está vacío o solo tiene null/undefined, retornar undefined
    if (cleanedFiles.length === 0 || cleanedFiles.every((file: any) => file === null || file === undefined)) {
      return undefined;
    }
    
    return cleanedFiles;
  }
  
  
  if (files && typeof files === 'object') {
    if(files.state) return undefined;

    delete files.__typename;
    return files;
  }
  
  return files;
};

const CardOwnership = ({
  ownership,
  disabledForm,
  ownerships,
  closeTask,
  isActionRemoved,
  entity,
  onDeleteOwner,
  isExpand,
  index,
  setNewPercentageLiveEdit,
  handlerSwitchExpandCard,
}: Props) => {
  const [form] = Form.useForm();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loan: Loan | any = useContext(LoanContext);
  const getTotal = useContext(TotalPercentageContext);

  const { data: responseCreateOwner, fetching: loadingCreateOwner, execute: fetchCreateOwnership } = useCreateOwnership();
  const { data: createResponse, fetching, execute: createResponseTask } = useCreateResponseTask();
  const { data: responseRemove, fetching: loadingRemove, execute: removeTaskResponse } = useRemoveTaskResponse();
  const { execute: deleteOwner } = useDeleteOwnership();
  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: ownership.taskResponse.task._id } },
    pause: true
  });
  const { data: taskComments } = useGetTaskResponseComments({
    variables: { taskId: ownership.taskResponse.task._id, state: 'pending' }
  });

  const [modalComplete, setModalComplete] = useState<boolean>(false);
  const [modalFinishTask, setModalFinishTask] = useState<boolean>(false);
  const [saveEdit, setSaveEdit] = useState<string>();
  const [percentageOwner, setPercentageOwner] = useState<number>();
  const [pendingUpdate, setPendingUpdate] = useState<any>();
  const [showErrorBox, setShowErrorBox] = useState(false);
  const [isStep3, setStep3] = useState(false);
  const [isTextDataError, setTextDataError] = useState<boolean>();
  const [showErrorBoxTotalPercentage, setShowErrorBoxTotalPercentage] = useState(false);

  useEffect(() => {
    getTaskResponses({
      requestPolicy: 'network-only',
      variables: { taskId: ownership.taskResponse.task._id }
    });
  }, [])

  useEffect(() => {
    const step3 = taskResponses?.find((item: any) => item.step === 3);
    setStep3(!!step3);
  }, [taskResponses]);

  useEffect(() => {
    if (responseCreateOwner) {
      const ownerId = responseCreateOwner._id;
      const payload = pendingUpdate;

      if (payload) {
        const { files, ...pendingCreateResponse } = payload;
        pendingCreateResponse.metadata.ownerId = ownerId;
        createResponseTask(pendingCreateResponse);
      }
    }
  }, [responseCreateOwner])

  useEffect(() => {
    if (responseRemove)
      getTaskResponses({
        requestPolicy: 'network-only',
        variables: { taskId: ownership.taskResponse.task._id }
      });
  }, [responseRemove]);

  useEffect(() => {
    if (ownership?.ownership) setPercentageOwner(ownership.ownership);
  }, [ownership]);

  useEffect(() => {
    if (!isUpdatePercentage) form.resetFields()
  }, [ownership]);

  useEffect(() => {
    if (createResponse) {
      setSaveEdit('saved');

      //Cerrar card cuando esta completo
      //No agregar el ownership.state !== Draft, porque en el caso que ya este en draft y se quiera editar, se va a cerrar, es un comportamiento raro
      if (createResponse.state === TaskState.Draft && ownership.state !== TaskState.RequestError)
        changeEdit('close');
    }
  }, [createResponse]);

  useEffect(() => {
    if (taskComments) {
      const issue = taskComments?.find(item => item.taskResponse._id === ownership.taskResponse._id && ownership.state === TaskState.RequestError);

      let textDataError = issue?.metadata?.dataOrDocumentSelected.length === 1 && issue?.metadata?.dataOrDocumentSelected.includes('Text/data');

      setTextDataError(textDataError);
    }
  }, [taskComments]);

  const changeEdit = (newState?: 'close') => {
    if (handlerSwitchExpandCard)
      handlerSwitchExpandCard(ownership._id || (ownership as any).ownerId, newState === 'close');
    setSaveEdit('');
  };

  const saveChanges = async (values: any, files: any, newState?: TaskState) => {
    isUpdatePercentage = false;
    
    if(ownership.state === TaskState.Review){
      changeEdit('close');
      return;
    }

    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    let changeState = taskResponse.state;

    if (
      taskResponse.state === TaskState.Incomplete &&
      isValidForm(values, files) &&
      entity?._id
    ) {
      const { __typename, _id, ownerId, referenceId, loans, ...metadata } = taskResponse.metadata;

      const cleanedFiles = cleanFileLocalUpload(files);
      await fetchCreateOwnership({
        ...metadata,
        ...values,
        filesExists: ownership?.files,
        files: cleanedFiles,
        entity: entity._id,
        loan: loan._id,
        state: TaskState.Draft,
        stakes: {
          [entity._id]: ownership.ownership
        },
        _id: ownerId || _id
      });
      //createGuarantorState usarlo en el primer draft
      changeState = TaskState.Draft;
    } else if (
      taskResponse.state !== TaskState.Incomplete &&
      (taskResponse.state === TaskState.RequestError && isValidForm(values, files, true, isTextDataError))
    ) {
      //Sucede cuando la respuesta esta en error y debe rellenar todo el formulario de nuevo, para que el estado vuelva a cambiar a draft

      let totalPercentage = getTotal();
      setShowErrorBoxTotalPercentage(totalPercentage !== 100);

      if (totalPercentage !== 100) {
        changeState = TaskState.RequestError;
      } else {
        changeState = TaskState.Review;
      }
    }

    if (newState) changeState = newState;
    
    //files puede llegar como un JSON y no como un tipo File, en este caso no se deben enviar los archivos en files, sino en filesExist
    const cleanedFiles = cleanFileLocalUpload(files);
    const payload = {
      ...taskResponse,
      task: task._id,
      state: changeState,
      metadata: { ...taskResponse.metadata, ...values },
      files: cleanedFiles?.[0]?.key ? null : cleanedFiles
    };

    //Se guarda el payload de la nueva taskResponse, y se usa para ACTUALIZAR el taskResponse con el ownerId nuevo
    //Se usa en el useEffect que observa el responseCreateOwner
    setPendingUpdate(payload);
    await createResponseTask(payload);
  };

  const verifyProfile = async () => {
    isUpdatePercentage = false;

    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    await createResponseTask({
      ...taskResponse,
      task: task._id,
      state: TaskState.Draft,
      metadata: taskResponse.metadata,
    });
  }

  const changeOwnership = async (_ownership: any) => {
    isUpdatePercentage = true;
    
    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    let isCompleteProfile = false;
    
    if (_ownership && entity &&
      taskResponse.metadata.address &&
      taskResponse.metadata.dateBirth &&
      taskResponse.metadata.email &&
      taskResponse.metadata.phone &&
      taskResponse.metadata.ssn &&
      ignoreFile?.length > 0
    ) {
      isCompleteProfile = true;
      const { _id, ownerId, referenceId, loans, ...metadata } = taskResponse.metadata;
      
      await fetchCreateOwnership({
        ...metadata,
        stakes: { ...ownership.stakes, [entity._id]: _ownership },
        entity: entity._id,
        loan: loan._id,
        _id: ownerId || _id
      });
    }

    let newState = _ownership < 20 ? TaskState.Draft :
      isCompleteProfile && !taskResponse?.metadata?._id ? TaskState.Draft : TaskState.Incomplete;

    let isTaskResponseState = taskResponse.state === TaskState.RequestError;

    if (isTaskResponseState)
      newState = TaskState.RequestError;

    //Ignorar atributo loans, ya que no se debe enviar en la metadata
    const { loans, ...metadata } = taskResponse.metadata;

    await createResponseTask({
      ...taskResponse,
      task: task._id,
      state: newState,
      metadata: { ...metadata, ownership: _ownership }
    });

    setNewPercentageLiveEdit(null);
  };

  const deleteOwnership = async () => {
    removeTaskResponse({ taskResponseId: ownership.key });

    const ownerId = ownership?._id || (ownership as any)?.ownerId;

    if (ownerId)
      deleteOwner({ ownerId, entityId: entity._id, loanId: loan._id });

    if (onDeleteOwner)
      onDeleteOwner(ownerId);
  }

  const debouncedChange = useCallback((value: number) => {
    if (value > 0) {
      changeOwnership(value);
    }
    setNewPercentageLiveEdit({
      ownerId: (ownership._id || (ownership as any).ownerId),
      value
    });
  }, [changeOwnership, ownership]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChangeOwnership = (value: number | null) => {
    // Permitir valores null/undefined para campos vacíos
    setPercentageOwner(value || undefined);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      debouncedChange(value || 0);
    }, 500);
  };

  const handleBlurOwnership = () => {
    // Si el valor está vacío (undefined) o es 0, establecer 1
    if (percentageOwner === undefined || percentageOwner === 0) {
      setPercentageOwner(1);
      debouncedChange(1);
    }
  };

  const isValidForm = (values: any, files: any, ignoreOwnershipData?: boolean, ignoreFiles?: boolean) => {
    return (values.address || (!ignoreOwnershipData && ownership.address)) &&
      (values.dateBirth || (!ignoreOwnershipData && ownership.dateBirth)) &&
      (values.email || (!ignoreOwnershipData && ownership.email)) &&
      (values.phone || (!ignoreOwnershipData && ownership.phone)) &&
      (values.ssn || (!ignoreOwnershipData && ownership.ssn)) &&
      (ignoreFiles || files?.length > 0 || (!ignoreOwnershipData && ownership?.files?.length > 0)) &&
      (values.ownership || ownership.ownership);
  }

  const getEffectiveState = (): TaskState => (ownership.state as TaskState);

  const effectiveState = getEffectiveState();

  type TagOwnerStatus = 'no-guarantor' | 'error' | 'next-step' | 'incomplete' | 'verify' | 'complete';

  const tagOwnerState: TagOwnerStatus = (() => {
    const rules: Array<{ match: boolean; value: TagOwnerStatus }> = [
      {
        match: !ownership.ownership || ownership.ownership < 20,
        value: 'no-guarantor',
      },
      {
        match: effectiveState === TaskState.RequestError,
        value: 'error',
      },
      {
        match: !ownership._id && effectiveState === TaskState.Incomplete,
        value: 'incomplete',
      },
      {
        match:
          Boolean(ownership._id) &&
          effectiveState === TaskState.Incomplete &&
          isValidForm(ownership, ownership.files),
        value: 'verify',
      },
      {
        match:
          Boolean(ownership._id) &&
          effectiveState === TaskState.Incomplete &&
          !isValidForm(ownership, ownership.files),
        value: 'incomplete',
      },
    ];

    return rules.find(rule => rule.match)?.value ?? 'complete';
  })();

  // Deshabilitar eliminar si:
  // 1. Está cargando la eliminación
  // 2. Es el primer ownership y es el único que no está completo (debe quedar al menos uno completo)
  // 3. Es el primer ownership y es el único (debe quedar al menos uno)
  const disabledRemove = loadingRemove ||
    (index === 0 && ownerships?.filter(item => item.state !== TaskState.Incomplete)?.length === 1) ||
    (index === 0 && ownerships?.length === 1) ||
    disabledForm;

  useEffect(() => {
    if (!disabledRemove || !(ownerships?.length === 1)) setShowErrorBox(false);
  }, [disabledRemove, ownerships])

  let totalPercentage = getTotal();

  return (
    <div style={{ position: 'relative' }} id={ownership.key} className={`card-ownership ${isExpand && 'card-ownership-expand'}`}>
      {isActionRemoved && (
        <>
          <CloseCircleFilled
            className={`icon-delete ${(disabledRemove) && 'icon-delete-disabled'}`}
            onClick={!disabledRemove ? deleteOwnership : () => { }}
            disabled={disabledRemove}
          />
          {disabledRemove && (
            <div
              onClick={() => setShowErrorBox(true)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                cursor: 'not-allowed',
                backgroundColor: 'transparent'
              }}
            />
          )}
          {showErrorBox && disabledRemove && (
            <ErrorBox text='You must have at least one guarantor on this loan' img={alertIcon} className="mb-10" />
          )}
        </>
      )}

      <>
        <CloseCircleFilled
          className={`icon-delete ${(disabledRemove) && 'icon-delete-disabled'}`}
          onClick={!disabledRemove ? deleteOwnership : () => { }}
          disabled={disabledRemove}
        />
        {disabledRemove && (
          <div
            onClick={() => setShowErrorBox(true)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: 'not-allowed',
              backgroundColor: 'transparent'
            }}
          />
        )}
      </>
      <div
        className={`content-card ${'content-card-' + effectiveState} ${!isActionRemoved ? 'content-card-hover-removed' : ''}`}
        style={{
          height: !isExpand ? 74 : 'auto',
          padding: !isExpand ? '5px 18px 5px 16px' : '10px 18px 5px 16px'
        }}
      >
        <div className={`card-ownership`}>
          <div className="section-1">
            {isExpand &&
              <StateTag
                state={effectiveState}
                isLabelLocked
                isOtherState={effectiveState === TaskState.Draft}
                colorState={effectiveState === TaskState.Draft ? TaskState.Draft : undefined}
                isDraftState={effectiveState === TaskState.Draft}
              />
            }
            {isExpand &&
              <div className="names" style={{ marginTop: 7 }}>
                <div className="name">
                  <label className="body-regular text-blue">First name</label>
                  <span>{ownership.name}</span>
                </div>
                {ownership.lastname && (
                  <div className="name">
                    <label className="body-regular text-blue">Last name</label>
                    <span>{ownership.lastname}</span>
                  </div>
                )}
              </div>
            }
            {!isExpand &&
              <div className="resume-names" style={{ marginTop: 7 }}>
                <StateTag
                  state={effectiveState}
                  isOtherState={effectiveState === TaskState.Draft}
                  isLabelLocked
                  colorState={effectiveState === TaskState.Draft ? TaskState.Draft : effectiveState}
                  isDraftState={effectiveState === TaskState.Draft}
                />
                <div className="name">
                  <span>{ownership.name + ' ' + ownership.lastname}</span>
                </div>
              </div>
            }
            <TagOwnerState
              className="tag-owner-state"
              onChange={changeEdit}
              isExpand={isExpand}
              state={tagOwnerState}
              disabled={effectiveState === TaskState.Approved && !isStep3}
              disabledExpand={!isStep3}
            />
          </div>
          <div className="section-2">
            {!isExpand &&
              <div className='ownership-section'>
                {percentageOwner && percentageOwner >= 20 &&
                  <span className="body-regular body-regular-w5 guarantor">
                    <img src={guarantorYellow} className="lienzo" alt="lienzoSvg" />{' '}
                    Guarantor
                  </span>
                }
                <InputBorderNone
                  type="number"
                  placeholder="Ownership"
                  suffix={percentageOwner ? '%' : ' '}
                  className={`input-disabled-color ${!percentageOwner && 'suffix-null'} mt-0`}
                  disabled={
                    effectiveState !== TaskState.Incomplete &&
                    effectiveState !== TaskState.RequestError &&
                    effectiveState !== TaskState.Draft
                  }
                  color="warning"
                  width={percentageOwner ? '50px' : '80px'}
                  max={100}
                  min={1}
                  value={percentageOwner}
                  onChange={(value: number) => {
                    setPercentageOwner(value);
                    setNewPercentageLiveEdit({ ownerId: (ownership._id || (ownership as any).ownerId), value });
                  }}
                  onBlur={(e: any) => {
                    if (e.target.value > 0) changeOwnership(parseInt(e.target.value));
                  }}
                  deletefull={true}
                />
              </div>
            }

            {isExpand && <>
              {percentageOwner && (
                <span
                  className="body-regular text-blue"
                  style={{ fontSize: 12, marginTop: 5 }}
                >
                  Ownership
                </span>
              )}

              <InputBorderNone
                type="number"
                placeholder="Ownership"
                suffix={percentageOwner ? '%' : ' '}
                className={`input-disabled-color ${!percentageOwner && 'suffix-null mt-20'}`}
                color="warning"
                height={20}
                width={'100px'}
                max={100}
                min={1}
                value={percentageOwner}
                disabled={disabledForm}
                onChange={handleChangeOwnership}
                onBlur={handleBlurOwnership}
                deletefull={true}
              />
              {percentageOwner && percentageOwner >= 20 && isExpand && (
                <span className="body-regular body-regular-w5 guarantor">
                  <img src={guarantorYellow} className="lienzo" alt="lienzoSvg" />{' '}
                  Guarantor
                </span>
              )}
            </>
            }
          </div>
        </div>
        {isExpand && (
          <>
            <FormEdit
              ownership={ownership}
              ownerships={ownerships}
              disabledForm={disabledForm}
              saveChanges={saveChanges}
              saveEdit={fetching ? 'loading' : saveEdit}
              fetching={fetching || loadingCreateOwner}
              verifyProfile={verifyProfile}
              isExpand={isExpand}
              createResponse={createResponse}
              taskComments={taskComments}
            />
          </>
        )}

        {showErrorBoxTotalPercentage && totalPercentage !== 100 && (
          <ErrorBox text='Total percentage must be 100%' img={alertIcon} className="mb-10" />
        )}

        {isExpand &&
          <IssuesTaskResponse
            taskId={ownership.taskResponse.task._id}
            taskResponseId={ownership.taskResponse._id}
            componentDataError={(textDataError: any) => (
              <div className='text-data'>
                <div className='row'>
                  <span>SSN</span>
                  <b>{textDataError.ssn}</b>
                </div>
                <div className='row'>
                  <span>Date of birth</span>
                  <b>{moment(textDataError.dateBirth).format(DATE_FORMAT)}</b>
                </div>
                <div className='row'>
                  <span>Phone number</span>
                  <b>{textDataError.phone}</b>
                </div>
                <div className='row'>
                  <span>Email address</span>
                  <b>{textDataError.email}</b>
                </div>
                <div className='row'>
                  <span>Home address</span>
                  <b>{textDataError.address}</b>
                </div>
              </div>
            )}
          />
        }

        <ModalCompleteProfile
          visible={modalComplete}
          setVisible={setModalComplete}
          closeTask={closeTask}
        />
        <ModalFinishTask
          visible={modalFinishTask}
          setVisible={setModalFinishTask}
          closeTask={closeTask}
        />
      </div>
    </div>
  );
};
export default CardOwnership;
